import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { makeStyles } from "tss-react/mui";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MapIcon from "@mui/icons-material/Map";
import RswState from "../../../enums/robot/rsw/rsw-state";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import ListSubheader from "@mui/material/ListSubheader";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Divider from "@mui/material/Divider";
import EditIcon from "@mui/icons-material/Edit";
import { FilterKeys } from "../../../enums/query-params/filters";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { RoomMiniOption } from "./RoomSelect";
import RoomSelector from "./RoomSelector";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import ReadOnlyProtectedIconButton from "../../Inputs/ReadOnlyProtectedIconButton";
import {
  selectCloudHasUnsyncedChanges,
  selectIsAdvancedModeEnabled,
  selectIsWebApp,
  selectWebappHasUnsyncedChanges,
} from "../../../store/app-configurations/app-configurations-selectors";
import { selectCurrentRobotState } from "../../../store/status/status-selectors";
import {
  selectFloors,
  selectFloorsRestSet,
  selectRooms,
  selectSelectedRoom,
  selectSelectedRoomId,
} from "../../../store/room/room-selectors";
import CustomAlert from "../../Feedback/CustomAlert";
import CustomLoader from "../../Feedback/CustomLoader";
import ForceSyncButton from "../../Websockets/ForceSyncButton";
import WebAppNavigationRoutes from "../../../enums/query-params/webapp-navigation-routes";

const useStyles = makeStyles()((theme) => ({
  scrollbar: {
    "&::-webkit-scrollbar": {
      width: theme.spacing(1.8),
    },
    "&::-webkit-scrollbar-track": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[100],
    },
    "&::-webkit-scrollbar-thumb": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

const RoomDependentWrapperPage: React.FC<{
  children: React.ReactNode;
  showFloorSelect?: boolean;
  scrollable?: boolean;
  hideFloorAndRoomSelection?: boolean;
  navBarComponents?: React.ReactNode;
  RoomUploadComponent?: React.ComponentType<{ isOpen: boolean; onClose: () => void }>;
  onEditFloorClickHandler?: (floorId: string) => void;
}> = ({
  scrollable,
  children,
  showFloorSelect,
  hideFloorAndRoomSelection,
  navBarComponents,
  onEditFloorClickHandler,
  RoomUploadComponent,
}) => {
  const { t } = useTranslation();

  const { classes } = useStyles();
  const theme = useTheme();
  const matchesMdDown = useMediaQuery(() => theme.breakpoints.down("md"));

  const dispatch = useSharedDispatch();
  const navigate = useNavigate();

  const { setSelectedRoomId } = useSharedActions();

  const [queryParams, setQueryParams] = useSearchParams();
  // this thing always returns a string so it can contain the string "null" but never null, so we adapt for that
  const queryRoomId = queryParams.get(FilterKeys.RoomId) === "null" ? null : queryParams.get(FilterKeys.RoomId);

  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));
  const rooms = useSharedSelector((state) => selectRooms(state));
  const floors = useSharedSelector((state) => selectFloors(state));
  const floorsRestSet = useSharedSelector((state) => selectFloorsRestSet(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const selectedRoom = useSharedSelector((state) => selectSelectedRoom(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const selectedRoomId = useSharedSelector((state) => selectSelectedRoomId(state));
  const webappHasUnsyncedChanges = useSharedSelector((state) => selectWebappHasUnsyncedChanges(state));
  const cloudHasUnsyncedChanges = useSharedSelector((state) => selectCloudHasUnsyncedChanges(state));

  const [isRoomUploadOpen, setIsRoomUploadOpen] = useState(false);

  const defaultRoomId = useMemo(
    () =>
      [...rooms]
        .sort((a, b) => {
          if (a.secondaryStation != null) {
            return 1;
          }
          if (b.secondaryStation != null) {
            return -1;
          }
          return a.name > b.name ? 1 : -1;
        })
        .find((r) => r.isArchived === false)?.id,
    [rooms],
  );

  useEffect(() => {
    const matchedRoom = rooms.find((r) => r.id === queryRoomId);
    const needsRedirect =
      (floorsRestSet.getStatus === HttpFetchStatus.Success && matchedRoom == null) || matchedRoom?.isArchived;
    if (!needsRedirect) {
      return;
    }
    // Only navigate when the query params actually change. `setQueryParams`
    // (react-router) gets a new identity every time the location changes, so
    // calling it unconditionally here re-triggers this effect on the resulting
    // re-render — an infinite navigate loop when there is nothing to update
    // (e.g. no rooms and no RoomId param). Guard each branch accordingly.
    if (defaultRoomId != null) {
      setQueryParams((prevState) => {
        prevState.set(FilterKeys.RoomId, defaultRoomId);
        return prevState;
      });
    } else if (queryRoomId != null) {
      setSelectedRoomId(null);
      setQueryParams((prevState) => {
        prevState.delete(FilterKeys.RoomId);
        return prevState;
      });
    }
  }, [rooms, defaultRoomId, queryRoomId, setQueryParams, setSelectedRoomId, floorsRestSet.getStatus]);

  useEffect(() => {
    if (queryRoomId != null && queryRoomId !== selectedRoomId) {
      dispatch(setSelectedRoomId(queryRoomId));
    } else if (selectedRoom != null && queryRoomId == null) {
      if (rooms.find((r) => r.id === selectedRoomId && r.isArchived === false) != null) {
        setQueryParams((prevState) => {
          prevState.set(FilterKeys.RoomId, selectedRoomId!);
          return prevState;
        });
      } else {
        dispatch(setSelectedRoomId(null));
      }
    }
  }, [rooms, dispatch, queryRoomId, selectedRoomId, setQueryParams, selectedRoom, setSelectedRoomId]);

  useEffect(() => {
    if (queryRoomId == null && selectedRoomId == null && defaultRoomId != null) {
      let roomId = defaultRoomId;
      if (currentRobotState?.status?.roomId != null) {
        roomId = currentRobotState.status.roomId;
      }
      if (roomId != null) {
        setQueryParams((prevState) => {
          prevState.set(FilterKeys.RoomId, roomId);
          return prevState;
        });
      }
    }
  }, [currentRobotState?.status?.roomId, defaultRoomId, queryRoomId, selectedRoomId, setQueryParams]);

  const roomSelectChangeHandler = (event: SelectChangeEvent<string>, _args: React.ReactNode) => {
    if (event.target.value == null) return;
    setQueryParams((prevState) => {
      prevState.set(FilterKeys.RoomId, event.target.value);
      return prevState;
    });
  };

  const roomSideChangeHandler = useCallback(
    (roomId: string) => {
      setQueryParams((prevState) => {
        prevState.set(FilterKeys.RoomId, roomId);
        return prevState;
      });
    },
    [setQueryParams],
  );

  const roomMappingOpenClickHandler = () => {
    navigate(WebAppNavigationRoutes.routeRoomMapping);
  };

  const roomUploadClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsRoomUploadOpen(true);
  };

  const roomUploadCloseHandler = () => {
    setIsRoomUploadOpen(false);
  };

  const showRoomSideSelection = !showFloorSelect && !hideFloorAndRoomSelection && !matchesMdDown;
  const isRobotInIdleState =
    currentRobotState?.status?.state === RswState.Idle || currentRobotState?.status?.state === RswState.Ready;
  const uniqueFloorIds = Array.from(new Set(rooms.filter((r) => !r.isArchived).map((r) => r.floorId)));
  const roomSelectOptions = useMemo(() => {
    const options: React.ReactNode[] = [];
    uniqueFloorIds.forEach((floorId, index) => {
      options.push(
        <ListSubheader
          key={"floor_" + floorId}
          sx={{
            pt: index === 0 ? undefined : 2,
            backgroundColor: "transparent",
            marginBottom: showRoomSideSelection ? -1 : undefined,
          }}
        >
          {floors.find((f) => f.id === floorId)?.name}
        </ListSubheader>,
      );
      rooms
        .filter((r) => r.floorId === floorId && r.isArchived === false)
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        })
        .forEach((r, index) => {
          if (index !== 0) {
            options.push(<Divider key={r.id + "_divider"} style={{ marginTop: 0, marginBottom: 0 }} />);
          }
          if (showRoomSideSelection) {
            options.push(<RoomSelector key={r.id} room={r} onClick={() => roomSideChangeHandler(r.id)} />);
          } else {
            options.push(
              <MenuItem key={r.id} value={r.id}>
                <RoomMiniOption roomMetaInfo={r} />
              </MenuItem>,
            );
          }
        });
    });
    return options;
  }, [uniqueFloorIds, floors, rooms, showRoomSideSelection, roomSideChangeHandler]);

  const floorSelectOptions = useMemo(
    () =>
      uniqueFloorIds.map((floorId) => (
        <MenuItem
          key={floorId}
          value={
            rooms.find((r) => r.floorId === floorId && r.id === selectedRoomId)?.id ??
            rooms.find((r) => r.floorId === floorId)?.id
          }
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} width="100%">
            <Typography variant="inherit">{floors.find((f) => f.id === floorId)?.name}</Typography>
            {onEditFloorClickHandler != null && (
              <ReadOnlyProtectedIconButton
                color="success"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditFloorClickHandler(floorId);
                }}
              >
                <EditIcon />
              </ReadOnlyProtectedIconButton>
            )}
          </Stack>
        </MenuItem>
      )),
    [rooms, floors, onEditFloorClickHandler, selectedRoomId, uniqueFloorIds],
  );

  const newRoomButtons = isWebapp ? (
    <React.Fragment>
      <Tooltip
        title={
          currentRobotState?.status?.state == null
            ? t("mapping-robot-unknown-state-error")
            : !isRobotInIdleState
              ? t("mapping-robot-not-idle-warning")
              : undefined
        }
      >
        <span>
          <ReadOnlyProtectedButton
            startIcon={<MapIcon />}
            onClick={roomMappingOpenClickHandler}
            fullWidth
            size="large"
            disabled={!isRobotInIdleState}
            id="dashboard-new-mapping-button"
          >
            {t("new-room-mapping")}
          </ReadOnlyProtectedButton>
        </span>
      </Tooltip>
      {isAdvancedModeEnabled && showFloorSelect && (
        <ReadOnlyProtectedButton fullWidth size="large" startIcon={<FileUploadIcon />} onClick={roomUploadClickHandler}>
          {t("room-upload")}
        </ReadOnlyProtectedButton>
      )}
    </React.Fragment>
  ) : null;

  return (
    <Box sx={{ height: "100%" }}>
      {rooms.length > 0 && selectedRoomId != null && selectedRoomId.length > 0 ? (
        <Stack direction="row" height="100%">
          {showRoomSideSelection && (
            <React.Fragment>
              <Stack sx={{ height: "100%" }}>
                {newRoomButtons}
                <Box className={classes.scrollbar} sx={{ height: "100%", overflow: "auto" }}>
                  {roomSelectOptions}
                </Box>
              </Stack>
              <Divider orientation="vertical" flexItem variant="middle" sx={{ p: 1 }} />
            </React.Fragment>
          )}
          <Stack spacing={0.5} sx={{ overflow: "hidden", width: "100%" }}>
            {!hideFloorAndRoomSelection && (matchesMdDown || !showRoomSideSelection) ? (
              <AppBar position="static" color="transparent">
                <Toolbar variant="dense" sx={{ justifyContent: "space-between" }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      overflowX: "auto",
                      flexGrow: 1,
                    }}
                  >
                    <Select
                      value={selectedRoomId}
                      onChange={roomSelectChangeHandler}
                      sx={(theme) => ({
                        border: "none",
                        "& .MuiSelect-select": { px: 2, py: 0.5 },
                        minWidth: theme.spacing(24),
                      })}
                      variant="standard"
                      renderValue={(roomId) =>
                        showFloorSelect ? (
                          <Typography variant="inherit" sx={(theme) => ({ lineHeight: theme.spacing(5) })}>
                            {floors.find((f) => f.id === rooms.find((r) => r.id === roomId)?.floorId)?.name}
                          </Typography>
                        ) : (
                          <RoomMiniOption roomMetaInfo={rooms.find((r) => r.id === roomId)!} />
                        )
                      }
                    >
                      {showFloorSelect ? floorSelectOptions : roomSelectOptions}
                      {isWebapp && <Divider orientation="horizontal" />}
                      {newRoomButtons}
                    </Select>
                  </Stack>
                </Toolbar>
              </AppBar>
            ) : navBarComponents != null ? (
              navBarComponents
            ) : null}
            <Stack
              sx={{
                overflow: "hidden",
                flexGrow: 1,
                pl: { xs: 0, md: 2 },
                height: "100%",
              }}
            >
              <Box flexShrink={0}>
                <Collapse in={webappHasUnsyncedChanges || cloudHasUnsyncedChanges}>
                  <CustomAlert
                    sx={{ mb: 2 }}
                    severity="info"
                    header={
                      webappHasUnsyncedChanges
                        ? t("info-webapp-has-unsynced-changes")
                        : t("info-sphere-has-unsynced-changes")
                    }
                    action={<ForceSyncButton />}
                  />
                </Collapse>
              </Box>
              <Box flex={1} sx={{ overflow: scrollable ? "auto" : "hidden" }}>
                {children}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      ) : floorsRestSet.getStatus === HttpFetchStatus.Idle || floorsRestSet.getStatus === HttpFetchStatus.Loading ? (
        <CustomLoader loadingMessage={t("loading-room")} />
      ) : (
        <React.Fragment>
          <Container maxWidth="sm">
            <Stack spacing={2} pt={2}>
              <Alert severity="info">{t("info-no-available-rooms")}</Alert>
              {newRoomButtons}
            </Stack>
          </Container>
        </React.Fragment>
      )}
      {RoomUploadComponent && <RoomUploadComponent isOpen={isRoomUploadOpen} onClose={roomUploadCloseHandler} />}
    </Box>
  );
};

export default RoomDependentWrapperPage;
